<script lang="ts">
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { xhr, toast, dateConverter } from "$app/utils"
  import ModifyStandard from "./Billing/ModifyStandard.svelte"
  import UpgradeFree from "./Billing/UpgradeFree.svelte"
  import CancelSubscription from "./Billing/CancelSubscription.svelte"
  import DeleteSubscription from "./Billing/DeleteSubscription.svelte"
  import { appStore } from "$app/store.svelte"
  import { CircleCheck } from "lucide-svelte"
  import { signupStore } from "$app/signupstore.svelte"

  let showUpgradeModifyStatus:
    | SubscriptionTypesType
    | "upgradefree"
    | "modifystandard" = $state(signupStore.subscription.subscription_type)
  let stripe_data: any = $state(null)
  let stripe_products: any[] = []
  let stripe_invoices: any[] = []
  let stripe_upcoming_invoice: any = $state(null)
  let stripe_subscription: any = $state(null)

  let isLoading = $state(true)
  async function initialize(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get(`/plans/${appStore.plan.id}/admin/billing`)

      let data = resp.data
      stripe_products = data.stripe_products
      signupStore.selectedPlan = stripe_products.find(
        (p) => p.id === "standard"
      )
      signupStore.subscription = data.subscription

      stripe_data = data.stripe_data
      signupStore.stripe_data = data.stripe_data
      if (data.stripe_invoices?.data) {
        stripe_invoices = data.stripe_invoices.data
      }
      stripe_upcoming_invoice = data.stripe_upcoming_invoice
      stripe_subscription = stripe_data.subscription
      appStore.users = appStore.convertUsersHash(data.users)
      console.log(data)
    } catch (e) {
      console.error(e)
      toast("There was an error loading the billing data", "error")
    } finally {
      isLoading = false
    }
  }

  onMount(async () => {
    initialize()
  })
</script>

<svelte:head>
  <script src="https://js.stripe.com/v3/"></script>
</svelte:head>

<div class="flex flex-col">
  <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
      <div class="flex justify-between items-center">
        <h2 class="text-2xl lg:text-3xl font-semibold pb-1 mb-3 text-secondary">
          Subscription
        </h2>
        <div>
          {#if showUpgradeModifyStatus === "upgradefree" && !isLoading}
            <button
              class=" !text-blue-500 px-4 border border-blue-100 py-2 rounded hover:bg-blue-50"
              onclick={() => (showUpgradeModifyStatus = "free")}
            >
              Cancel Upgrade
            </button>
          {/if}
        </div>
      </div>

      {#if isLoading}
        <div class="py-12 flex justify-center w-full">
          <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
        </div>
      {:else if showUpgradeModifyStatus === "upgradefree"}
        <div class="mt-4">
          <UpgradeFree
            oncomplete={() => {
              showUpgradeModifyStatus = "standard"
              window.location.reload()
            }}
          />
        </div>
      {:else if showUpgradeModifyStatus === "modifystandard"}
        <ModifyStandard
          oncancel={() => (showUpgradeModifyStatus = "standard")}
          oncomplete={() => (showUpgradeModifyStatus = "standard")}
        />
      {:else}
        <div class="border border-rounded border-gray-200 bg-white shadow">
          <div class="py-6 px-6">
            {#if signupStore.subscription.subscription_type === "free"}
              <div class="grid grid-cols-1 gap-3">
                <div class="flex justify-start items-center">
                  <p class="text-lg">
                    You are in the
                    <span class="font-bold">Free Subscription</span> plan
                  </p>
                  <div class="px-3">
                    <span
                      class="px-2 py-1 bg-blue-50 rounded text-gray-800 text-sm"
                    >
                      3 Full-Access Members
                    </span>
                  </div>
                </div>
                <div class="py-2">
                  <p class="text-lg text-gray-700">
                    Upgrade to our
                    <span class="font-bold">Standard Subscription</span>
                    to unlock the following features:
                  </p>
                  <ul
                    class="mt-4 ml-6 grid grid-cols-1 gap-y-3 text-base text-gray-600"
                  >
                    <li class="flex justify-start items-center">
                      <CircleCheck class="w-6 h-6 text-green-500 mr-2" />
                      Full Activity Page Access
                    </li>
                    <li class="flex justify-start items-center">
                      <CircleCheck class="w-6 h-6 text-green-500 mr-2" />
                      Unlimited Recognitions
                    </li>
                    <li class="flex justify-start items-center">
                      <CircleCheck class="w-6 h-6 text-green-500 mr-2" />
                      Quarterly and Annual Transition Meetings
                    </li>
                    <li class="flex justify-start items-center">
                      <CircleCheck class="w-6 h-6 text-green-500 mr-2" />
                      Bookshelf - Read Together
                    </li>
                    <li class="flex justify-start items-center">
                      <CircleCheck class="w-6 h-6 text-green-500 mr-2" />
                      Flywheel
                    </li>
                    <li class="flex justify-start items-center">
                      <CircleCheck class="w-6 h-6 text-green-500 mr-2" />
                      Push Notifications
                    </li>
                  </ul>
                </div>
              </div>
            {:else if signupStore.subscription.subscription_type === "unlimited"}
              <div class="grid grid-cols-1 gap-4">
                <div class="flex justify-start gap-4">
                  <p class="text-xl">
                    Legacy Business Success Plan Subscription
                  </p>
                  <div>
                    <span class="px-2 py-1 bg-blue-50 rounded text-gray-800"
                      >Unlimited Members</span
                    >
                  </div>
                </div>
                <div class="flex py-4 px-6">
                  <div
                    class="text-amber-700 text-sm bg-amber-50 p-2 border rounded border-amber-200 shadow text-center max-w-lg mx-auto"
                  >
                    You are subscribed to an unsupported legacy plan. Please
                    contact <a
                      class="text-blue-600 hover:underline"
                      href="mailto:support@leadwithpurpose.com"
                      >support@leadwithpurpose.com</a
                    > to switch to a supported plan.
                  </div>
                </div>
              </div>
            {:else if signupStore.subscription.subscription_type === "standard"}
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <h3 class="text-xl font-medium text-gray-900">
                    {signupStore.subscription.name}
                  </h3>
                </div>
                <div class="flex items-center gap-x-4">
                  <span
                    class="inline-flex items-center rounded-md bg-full-access-lt px-2 py-1 text-xs font-medium text-full-access-dk ring-1 ring-inset ring-full-access-md/10"
                  >
                    {signupStore.subscription.full_access_total} Full-Access Members
                  </span>
                  <span
                    class="inline-flex items-center rounded-md bg-team-member-lt px-2 py-1 text-xs font-medium text-team-member-dk ring-1 ring-inset ring-team-member-md/10"
                  >
                    {signupStore.subscription.team_member_total} Team Members
                  </span>
                  <span
                    class="inline-flex items-center rounded-md bg-recognition-lt px-2 py-1 text-xs font-medium text-recognition-dk ring-1 ring-inset ring-recognition-md/10"
                  >
                    {signupStore.subscription.recognition_member_total} Recognition
                    Members
                  </span>
                </div>
              </div>
              <div class="mt-6">
                <p class="text-gray-800 text-base">
                  With your Standard Subscription, you unlocked all features of
                  the platform.
                  {#if stripe_subscription?.status === "trialing"}
                    You are currently in your trial period, which will end on
                    <span class="font-bold"
                      >{dateConverter(stripe_data.subscription.trial_end)}</span
                    >.
                  {/if}
                </p>
              </div>
            {/if}
          </div>

          <div class="border-t border-gray-200">
            <div
              class="grid grid-cols-1 md:grid-cols-2 py-4 px-6 items-center justify-between"
            >
              <div>
                <p class="text-sm text-gray-800">
                  Have questions about your plan?
                  <a
                    class="ml-2 text-blue-600 hover:underline"
                    href="mailto:support@leadwithpurpose.com"
                    >Contact us Today</a
                  >
                </p>
              </div>
              <div class="flex justify-end items-center">
                {#if signupStore.subscription.subscription_type === "free"}
                  <button
                    onclick={() => (showUpgradeModifyStatus = "upgradefree")}
                    class="bg-accent hover:bg-lwp_blue hover:shadow py-3 px-6 text-core font-semibold text-md rounded-sm border border-gray-300"
                  >
                    Upgrade to Standard Subscription
                  </button>
                {:else if signupStore.subscription.subscription_type === "standard" && stripe_subscription}
                  <button
                    onclick={() => (showUpgradeModifyStatus = "modifystandard")}
                    class="bg-blue-100 hover:bg-blue-200 hover:shadow py-3 px-6 text-gray-700 rounded-sm text-sm border border-gray-300"
                  >
                    Modify Subscription
                  </button>
                {:else}
                  <p
                    class="bg-red-50 text-red-800 py-2 px-4 rounded italic text-xs mt-1 ml-2 text-center"
                  >
                    Your subscription is inactive, please
                    <a
                      href="mailto:support@leadwithpurpose.com"
                      class="hover:underline"
                    >
                      contact support@leadwithpurpose.com</a
                    > to activate your subscription.
                  </p>
                {/if}
              </div>
            </div>
          </div>
        </div>

        {#if signupStore.subscription.subscription_type === "standard"}
          <div
            class="mt-8 border border-rounded border-gray-200 bg-white shadow"
          >
            <div class="px-6 py-4">
              <h2 class="text-xl font-bold">
                Cancel Standard Subscription & Revert to Free Subscription
              </h2>

              <div>
                <div
                  class="rounded-md py-4 sm:flex sm:items-start sm:justify-between"
                >
                  <div class="grid grid-cols-2">
                    <div>
                      <div class="text-gray-600 text-sm">
                        Canceling your Standard Subscription will downgrade your
                        plan to the Free Subscription.
                      </div>
                    </div>
                    <div class="flex justify-end items-center">
                      <CancelSubscription
                        oncancel={() => {
                          initialize()
                          window.location.reload()
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <div class="mt-8 border border-rounded border-gray-200 bg-white shadow">
          <div class="px-6 py-4">
            {#if signupStore.subscription.subscription_type === "free"}
              <h2 class="text-xl font-bold">
                Delete Plan & Cancel Subscription
              </h2>
            {:else}
              <h2 class="text-xl font-bold">Request Plan Deletion</h2>
            {/if}
            <div>
              {#if appStore.plan.delete_requested_at}
                <div class="text-amber-600 text-base mt-6">
                  Your Plan has been queued for deletion. Please contact
                  support@leadwithpurpose.com if you have any concerns or want
                  to stop the deletion process.
                </div>
              {:else}
                <div
                  class="rounded-md py-4 sm:flex sm:items-start sm:justify-between"
                >
                  <div class="grid grid-cols-4">
                    <div class="col-span-3 text-sm text-gray-600">
                      {#if signupStore.subscription.subscription_type === "free"}
                        Deleting your account will automatically cancel your
                        free subscription and deletes all your plan data.
                      {:else}
                        Deleting your account is an irreversable process and
                        deletes all your plan data and cancels your active
                        subscription. If you want to proceed please click the
                        <span class="font-bold">Request Deletion</span> button and
                        we will get back to you within a Business Day to complete
                        your request.
                      {/if}
                    </div>
                    <div class="flex justify-end items-center">
                      <DeleteSubscription />
                    </div>
                  </div>
                </div>
              {/if}
            </div>
          </div>
        </div>
      {/if}
    </div>
  </div>
</div>
